import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, CalendarCheck, HelpCircle, type LucideProps } from 'lucide-react';
import { type ComponentType } from 'react';
import { cn } from '@tg-hookah/ui';

interface Tab {
  to: string;
  key: 'home' | 'booking' | 'faq';
  icon: ComponentType<LucideProps>;
}

const TABS: Tab[] = [
  { to: '/', key: 'home', icon: Home },
  { to: '/booking', key: 'booking', icon: CalendarCheck },
  { to: '/faq', key: 'faq', icon: HelpCircle },
];

export function BottomTabBar() {
  const { t } = useTranslation();
  return (
    <nav
      role="navigation"
      aria-label="primary"
      className="fixed bottom-0 inset-x-0 z-30 pb-[env(safe-area-inset-bottom)] pointer-events-none"
    >
      {/* Fade-out mask so content behind the bar blurs into the ink */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-6 inset-x-0 h-6"
        style={{ background: 'linear-gradient(to top, var(--ink-0), transparent)' }}
      />
      <div className="px-3 pb-2 pt-0 pointer-events-auto">
        <div className="relative overflow-hidden rounded-2xl surface-glass">
          <ul className="grid grid-cols-3">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <li key={tab.key}>
                  <NavLink
                    to={tab.to}
                    end={tab.to === '/'}
                    aria-label={t(`tabs.${tab.key}`)}
                    data-track={`click:tab-${tab.key}`}
                    className={({ isActive }) =>
                      cn(
                        'relative flex flex-col items-center justify-center py-2.5 gap-1 text-[11px] tracking-[0.08em] font-semibold transition-colors uppercase',
                        isActive ? 'text-white' : 'text-white/45',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Ember dot — the only warm accent in the whole chrome. */}
                        <span
                          aria-hidden
                          className={cn(
                            'absolute top-1 h-1 w-1 rounded-full transition-all duration-300',
                            isActive
                              ? 'opacity-100 scale-100'
                              : 'opacity-0 scale-50',
                          )}
                          style={
                            isActive
                              ? {
                                  background: 'var(--ember)',
                                  boxShadow:
                                    '0 0 8px 1px rgba(194,58,46,0.65), 0 0 18px 4px rgba(194,58,46,0.25)',
                                }
                              : undefined
                          }
                        />
                        <Icon
                          size={18}
                          strokeWidth={isActive ? 1.75 : 1.45}
                          className={cn(
                            'transition-transform duration-300',
                            isActive && 'scale-[1.06]',
                          )}
                        />
                        <span className="text-[10px] tracking-[0.14em]">
                          {t(`tabs.${tab.key}`)}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}
